import { DropDownList } from '@progress/kendo-react-dropdowns';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import React, { FormEvent, MouseEvent, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import NumberInput from 'src/components/NumberInput';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import TextInput from 'src/components/TextInput';
import useConfirm from 'src/hooks/useConfirm';
import useUpdateProductMutation from 'src/services/catalog_products/useUpdateProductMutation';
import { UpdateProductRequest } from 'src/types/catalog/UpdateProduct';
import { isNullOrUndefined } from 'src/utils';
import { useContextSelector } from 'use-context-selector';
import useEditItemInformationForm, { ProductData } from './useEditItemInformationForm';
import { ViewCatalogProductsContext } from './ViewCatalogProductsContext';

interface EditItemInformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  productData: ProductData;
  onRequestClose?: (cancelHandler: () => void) => void;
}

const EditItemInformationDialog: React.FC<EditItemInformationDialogProps> = ({
  isOpen,
  onClose,
  productData,
  onRequestClose,
}) => {
  const { confirm } = useConfirm();

  const selectedProduct = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.selectedProduct
  );

  const fetchProductDetails = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.fetchProductDetails
  );

  const fetchProducts = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.fetchProducts
  );

  const {
    formik,
    uomOptions,
    selectedUOM,
    uomFilterValue,
    handleUOMChange,
    handleUOMFilterChange,
    handleUOMBlur,
    getSaveButtonDisabledReason,
  } = useEditItemInformationForm({
    productData,
    isOpen,
  });

  const { mutateAsync: updateProduct, isPending: isSaving } = useUpdateProductMutation({
    onSuccess: async (): Promise<void> => {
      toast.success('Item details updated successfully.');
      // Close dialog immediately after toast appears
      formik.resetForm();
      onClose();
      // Continue fetching data in the background
      await fetchProductDetails();
      await fetchProducts();
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to update product. Please try again.';
      toast.error(`Error updating product: ${errorMessage}`);
    },
  });

  const handleSave = useCallback(async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (
      !selectedProduct?.productId ||
      !selectedProduct?.catalogId ||
      !selectedProduct?.categoryId
    ) {
      toast.error('Product information is missing. Please try again.');
      return;
    }

    const newUOMValue = formik.values.uom.trim();
    const originalUOMValue = formik.initialValues.uom.trim();

    let uomValue: string | null = null;

    if (!isEmpty(newUOMValue) && isEmpty(originalUOMValue)) {
      uomValue = newUOMValue;
    } else if (!isEmpty(newUOMValue) && !isEmpty(originalUOMValue)) {
      uomValue = newUOMValue;
    }

    const updateRequest: UpdateProductRequest = {
      productId: selectedProduct.productId,
      catalogId: selectedProduct.catalogId,
      categoryId: selectedProduct.categoryId,
      productName: formik.values.itemName.trim(),
      price: parseFloat(formik.values.price) || 0,
      brandName: formik.values.brandName.trim(),
      manufacturerName: formik.values.mfgName.trim(),
      manufacturerNumber: formik.values.manufacturerNumber.trim(),
      uom: uomValue,
      casePackUnits: formik.values.casePackUnits.trim(),
      weight: formik.values.weight.trim(),
      upc: formik.values.upc.trim(),
      unspsc: formik.values.unspsc.trim(),
    };

    await updateProduct(updateRequest);
  }, [formik, selectedProduct, updateProduct]);

  const handleCancel = useCallback(() => {
    const hasFormChanges = JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues);
    if (hasFormChanges) {
      confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to discard them?',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Yes, Discard',
        width: '500px',
      }).then((isAccepted) => {
        if (isAccepted) {
          formik.resetForm();
          onClose();
        }
      });
    } else {
      onClose();
    }
  }, [formik, confirm, onClose]);

  useEffect(() => {
    if (onRequestClose && isOpen) {
      onRequestClose(handleCancel);
    }
  }, [onRequestClose, handleCancel, isOpen]);

  const createFormikChangeHandler = (e: TextBoxChangeEvent) => {
    if (isSaving) {
      return;
    }
    const fieldName = e.syntheticEvent.target.name as keyof typeof formik.values;
    let value = (e.value as string) || '';

    // Clean numeric fields
    if (fieldName === 'price') {
      value = value.replace(/[^0-9.]/g, '');
    }

    // Create synthetic event for formik.handleChange
    const syntheticEvent = {
      target: {
        name: fieldName,
        id: fieldName,
        value: value,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    formik.handleChange(syntheticEvent);
    // Mark field as touched and validate on change
    formik.setFieldTouched(fieldName, true, false);
    formik.setFieldValue(fieldName, value, true);
  };

  const validationMessages = Object.values(formik.errors).filter((error) => error);
  const hasFormChanges = JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues);
  const isFormValid = Object.keys(formik.errors).length === 0;
  const lb = useMemo(() => selectedUOM.value.toLowerCase() === 'lb', [selectedUOM]);

  if (!isOpen) return null;

  return (
    <div className="bg-gray-100 mt-4">
      <form
        className="flex-1 p-0 px-1 flex flex-col overflow-auto"
        onSubmit={async (e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isSaving) {
            await handleSave();
          }
        }}
      >
        <div className="flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="flex flex-col gap-5">
              {/* Item Name */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="itemName">
                  Description
                </label>
                <TextInput
                  id="itemName"
                  name="itemName"
                  value={formik.values.itemName}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  className={clsx(
                    formik.errors.itemName && formik.touched.itemName && '!border-error',
                    '!font-bold [&_.k-input-inner]:!h-10'
                  )}
                  valid={!formik.touched.itemName || !formik.errors.itemName}
                  maxLength={500}
                  disabled={isSaving}
                />
                {formik.errors.itemName && formik.touched.itemName && (
                  <Error id="itemName">{formik.errors.itemName}</Error>
                )}
              </div>
              {/* UPC */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="upc">
                  UPC
                </label>
                <TextInput
                  id="upc"
                  name="upc"
                  value={formik.values.upc}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  valid={!formik.touched.upc || !formik.errors.upc}
                  className={`${
                    formik.errors.upc && formik.touched.upc ? '!border-error' : ''
                  } !font-bold [&_.k-input-inner]:!h-10`}
                  maxLength={50}
                  disabled={isSaving}
                />
                {formik.errors.upc && formik.touched.upc && (
                  <Error id="upc">{formik.errors.upc}</Error>
                )}
              </div>

              {/* Mfg Name */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="mfgName">
                  Mfg Name
                </label>
                <TextInput
                  id="mfgName"
                  name="mfgName"
                  value={formik.values.mfgName}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  valid={!formik.touched.mfgName || !formik.errors.mfgName}
                  className={`${
                    formik.errors.mfgName && formik.touched.mfgName ? '!border-error' : ''
                  } !font-bold [&_.k-input-inner]:!h-10`}
                  maxLength={180}
                  disabled={isSaving}
                />
                {formik.errors.mfgName && formik.touched.mfgName && (
                  <Error id="mfgName">{formik.errors.mfgName}</Error>
                )}
              </div>

              {/* Brand Name */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="brandName">
                  Brand Name
                </label>
                <TextInput
                  id="brandName"
                  name="brandName"
                  value={formik.values.brandName}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  valid={!formik.touched.brandName || !formik.errors.brandName}
                  className={`${
                    formik.errors.brandName && formik.touched.brandName ? '!border-error' : ''
                  } !font-bold [&_.k-input-inner]:!h-10`}
                  maxLength={180}
                  disabled={isSaving}
                />
                {formik.errors.brandName && formik.touched.brandName && (
                  <Error id="brandName">{formik.errors.brandName}</Error>
                )}
              </div>

              {/* Case Pack Units */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="casePackUnits">
                  Case Pack Units
                </label>
                <TextInput
                  id="casePackUnits"
                  name="casePackUnits"
                  value={formik.values.casePackUnits}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  valid={!formik.touched.casePackUnits || !formik.errors.casePackUnits}
                  className={`${
                    formik.errors.casePackUnits && formik.touched.casePackUnits
                      ? '!border-error'
                      : ''
                  } !font-bold [&_.k-input-inner]:!h-10`}
                  maxLength={50}
                  disabled={isSaving}
                />
                {formik.errors.casePackUnits && formik.touched.casePackUnits && (
                  <Error id="casePackUnits">{formik.errors.casePackUnits}</Error>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-5">
              {/* Price */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="price">
                  Price
                </label>
                <NumberInput
                  id="price"
                  name="price"
                  value={
                    isNullOrUndefined(formik.values.price) ? '' : formik.values.price.toString()
                  }
                  onChange={createFormikChangeHandler}
                  prefix="$"
                  decimal={lb ? 3 : 2}
                  valid={!formik.touched.price || !formik.errors.price}
                  className={`${
                    formik.errors.price && formik.touched.price ? '!border-error' : ''
                  } !font-bold [&_.k-input-inner]:!h-10`}
                  disabled={isSaving}
                  width={'100%'}
                />
                {formik.errors.price && formik.touched.price && (
                  <Error id="price">{formik.errors.price}</Error>
                )}
              </div>

              {/* UNSPSC */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="unspsc">
                  UNSPSC
                </label>
                <TextInput
                  id="unspsc"
                  name="unspsc"
                  value={formik.values.unspsc}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  valid={!formik.touched.unspsc || !formik.errors.unspsc}
                  className={`${
                    formik.errors.unspsc && formik.touched.unspsc ? '!border-error' : ''
                  } !font-bold [&_.k-input-inner]:!h-10`}
                  maxLength={105}
                  disabled={isSaving}
                />
                {formik.errors.unspsc && formik.touched.unspsc && (
                  <Error id="unspsc">{formik.errors.unspsc}</Error>
                )}
              </div>

              {/* Mfg # */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="manufacturerNumber">
                  Mfg #
                </label>
                <TextInput
                  id="manufacturerNumber"
                  name="manufacturerNumber"
                  value={formik.values.manufacturerNumber}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  valid={!formik.touched.manufacturerNumber || !formik.errors.manufacturerNumber}
                  className={`${
                    formik.errors.manufacturerNumber && formik.touched.manufacturerNumber
                      ? '!border-error'
                      : ''
                  } !font-bold [&_.k-input-inner]:!h-10`}
                  maxLength={60}
                  disabled={isSaving}
                />
                {formik.errors.manufacturerNumber && formik.touched.manufacturerNumber && (
                  <Error id="manufacturerNumber">{formik.errors.manufacturerNumber}</Error>
                )}
              </div>

              {/* UOM */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="uom">
                  UOM
                </label>
                <div
                  className={clsx(
                    '[&_.k-picker-solid]:bg-white [&_.k-picker-solid]:border-gray-600 [&_.k-picker-solid:hover]:bg-white [&_.k-picker-solid.k-hover]:bg-white [&_.k-picker-solid]:!h-10 [&_.k-input-inner]:!h-10 focus:[&_.k-picker-solid]:!border-[#86b7fe] focus:[&_.k-picker-solid]:!color-[#495057] focus:[&_.k-picker-solid]:!bg-[#ffffff] focus:[&_.k-picker-solid]:!shadow-[0_0_0_0.25rem_rgba(13,110,253,0.25)]',
                    formik.errors.uom &&
                      formik.touched.uom &&
                      '[&_.k-picker-solid]:!border-error [&_.k-input-inner]:!border-error'
                  )}
                >
                  <DropDownList
                    id="uom"
                    name="uom"
                    data={uomOptions}
                    value={selectedUOM}
                    textField="text"
                    dataItemKey="value"
                    onChange={handleUOMChange}
                    onFilterChange={handleUOMFilterChange}
                    onBlur={handleUOMBlur}
                    filterable
                    filter={uomFilterValue}
                    valid={!formik.touched.uom || !formik.errors.uom}
                    className={clsx(
                      '[&_.k-input-value-text]:!font-bold',
                      formik.errors.uom &&
                        formik.touched.uom &&
                        '[&_.k-input-inner]:!border-error [&_.k-picker-solid]:!border-error'
                    )}
                    size="small"
                    disabled={isSaving}
                  />
                </div>
                {formik.errors.uom && formik.touched.uom && (
                  <Error id="uom">{formik.errors.uom}</Error>
                )}
              </div>

              {/* Weight */}
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-500" htmlFor="weight">
                  Weight
                </label>
                <TextInput
                  id="weight"
                  name="weight"
                  value={formik.values.weight}
                  onChange={createFormikChangeHandler}
                  onBlur={formik.handleBlur}
                  valid={!formik.touched.weight || !formik.errors.weight}
                  className={`${
                    formik.errors.weight && formik.touched.weight ? '!border-error' : ''
                  } !font-bold [&_.k-input-inner]:!h-10`}
                  maxLength={50}
                  disabled={isSaving}
                />
                {formik.errors.weight && formik.touched.weight && (
                  <Error id="weight">{formik.errors.weight}</Error>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Controls */}
        <div className="px-4 py-3 flex items-center flex-shrink-0">
          <div className="flex-grow" />
          <div className="flex gap-2">
            <PopUp
              content={
                <div className="m-1 max-w-[300px]">
                  {validationMessages.length > 0 ? (
                    <PopUpContentWrapper type="Error" contents={validationMessages} />
                  ) : getSaveButtonDisabledReason(isSaving) ? (
                    <PopUpContentWrapper
                      type="Information"
                      contents={[getSaveButtonDisabledReason(isSaving)!]}
                    />
                  ) : null}
                </div>
              }
              show={Boolean(
                (!hasFormChanges || validationMessages.length > 0 || isSaving || !isFormValid) &&
                  (validationMessages.length > 0 || getSaveButtonDisabledReason(isSaving))
              )}
              anchorAlign={{ horizontal: 'center', vertical: 'top' }}
              popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
            >
              <Button
                color="success"
                onClick={async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleSave();
                }}
                onSubmit={async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
                  e.preventDefault();
                  e.stopPropagation();
                  await handleSave();
                }}
                disabled={!formik.dirty || isSaving || !formik.isValid}
                loading={isSaving}
              >
                Save
              </Button>
            </PopUp>
            <Button
              color="info"
              onClick={(e: MouseEvent<HTMLButtonElement>): void => {
                e.preventDefault();
                e.stopPropagation();
                handleCancel();
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditItemInformationDialog;
